import { useState, useEffect } from "react";
import type { TcsRecommendationEntity } from "../../domain/entities/TcsRecommendationEntity";
import { GetTcsRecommendationByJobIdUseCase } from "../../domain/use-cases/GetTcsRecommendationByJobIdUseCase";
import { tcsRecommendationRepository } from "../../data/repositories/TcsRecommendationRepositoryImpl";

const getTcsRecommendationByJobIdUseCase = new GetTcsRecommendationByJobIdUseCase(
  tcsRecommendationRepository
);

export interface UseTcsRecommendationReturn {
  tcsRecommendation: TcsRecommendationEntity | null;
  isLoading: boolean;
}

/**
 * Fetches TCS recommendation for a document once when the user lands on the review screen.
 *
 * @param jobId - The job ID (invoice/bill processing job_id)
 * @returns Object containing tcsRecommendation and loading state
 */
export function useTcsRecommendation(
  jobId: string | null
): UseTcsRecommendationReturn {
  const [tcsRecommendation, setTcsRecommendation] =
    useState<TcsRecommendationEntity | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!jobId) {
        setTcsRecommendation(null);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getTcsRecommendationByJobIdUseCase.execute(jobId);
        setTcsRecommendation(result);
      } catch (error) {
        console.warn("Failed to fetch TCS recommendation:", error);
        setTcsRecommendation(null);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [jobId]);

  return {
    tcsRecommendation,
    isLoading,
  };
}
